import { v4 as uuidv4 } from 'uuid';
import { getFanCompanions } from '../../config';
import { DemoFan } from '../../datas/demoFan';
import {
  createBusinessPlan,
  createGeneration,
  createPresentation,
  createWebpage,
  GenerationRequest,
} from '../../services/generationsApi';
import { capturePhoto } from '../capturePhoto';
import { isStationPhotoEnabled } from './helpers';
import { isStationModeEnabled } from './helpers';

const getAllFanCompanions = () => getFanCompanions(isStationModeEnabled());

// Default companion ID and slug (Lina - first station companion)
const DEFAULT_COMPANION_ID = 'ce8718be-1851-4c48-b369-2c472b9a42f0';
const DEFAULT_COMPANION_SLUG = 'lina-patel-14k9nml0n0';

export interface StartMusicGenerationParams {
  companionId: string;
  prompt: string;
  additionalData?: string;
  userPhotoUrl?: string;
}

export interface StartMusicGenerationResult {
  success: boolean;
  taskId?: string;
  error?: string;
}

export interface StartImageGenerationParams {
  companionId?: string;
  prompt: string;
}

export interface StartImageGenerationResult {
  success: boolean;
  error?: string;
}

export interface StartPresentationGenerationParams {
  companionId?: string;
  topic: string;
}

export interface StartPresentationGenerationResult {
  success: boolean;
  taskId?: string;
  error?: string;
}

export interface StartBusinessPlanGenerationParams {
  companionId?: string;
  topic: string;
}

export interface StartBusinessPlanGenerationResult {
  success: boolean;
  taskId?: string;
  error?: string;
}

export interface StartWebpageGenerationParams {
  companionId?: string;
  topic: string;
}

export interface StartWebpageGenerationResult {
  success: boolean;
  taskId?: string;
  error?: string;
}

export type SpacesUseCaseType = 'hospitality' | 'retail' | 'transit';

export interface StartSpacesUseCaseParams {
  type: SpacesUseCaseType;
}

export interface StartSpacesUseCaseResult {
  success: boolean;
  error?: string;
}

const findFanByCompanionId = (companionId: string): DemoFan | undefined => {
  return getAllFanCompanions().find((fan) => fan.companion_id === companionId);
};

let navigateFunction: ((path: string) => void) | null = null;

export const setNavigateFunction = (navigate: (path: string) => void) => {
  navigateFunction = navigate;
};

export const startMusicGeneration = async (
  params: StartMusicGenerationParams,
): Promise<StartMusicGenerationResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  try {
    window.dispatchEvent(
      new CustomEvent('stopGenerationAudio', {
        detail: {},
      }),
    );

    const { companionId, prompt, additionalData } = params;

    const fan = findFanByCompanionId(companionId);

    if (!fan) {
      return {
        success: false,
        error: `Companion with ID ${companionId} not found`,
      };
    }

    if (fan.capability !== 'music') {
      return {
        success: false,
        error: `Companion ${companionId} does not support music generation`,
      };
    }

    const uniqueId = uuidv4();
    const generationUrl = `/generation/${fan.slug}/${uniqueId}?start=true`;

    // Use provided userPhotoUrl if available, otherwise capture photo
    // unless the station was started with ?photo=false
    const userPhotoUrl =
      params.userPhotoUrl !== undefined
        ? params.userPhotoUrl
        : isStationPhotoEnabled()
        ? await capturePhoto()
        : null;

    const generationRequest: GenerationRequest = {
      prompt,
      musicStyle: fan.music_style,
      thumbnailUrl: fan.full_image_url,
      userPhotoUrl: userPhotoUrl || undefined,
      uniqueId,
      voiceId: fan.voice_id,
      language: fan.language ?? 'English',
      gender: fan.gender,
      artistTag:
        fan.artist_alias || fan.alias || fan.alias_tag || fan.given_name,
      additionalData: additionalData || undefined,
      useV2Api: true,
      stationMode: true,
      //numOutputs: 1,
    };

    createGeneration(generationRequest).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('Failed to create generation:', error);
      }
    });

    if (navigateFunction) {
      navigateFunction(generationUrl);
    } else {
      window.location.href = generationUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return {
      success: true,
      taskId: uniqueId,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start music generation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const startImageGeneration = async (
  params: StartImageGenerationParams,
): Promise<StartImageGenerationResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  try {
    const { prompt } = params;
    const companionId = params.companionId || DEFAULT_COMPANION_ID;

    // Look up companion, fallback to default slug if not found
    const fan = findFanByCompanionId(companionId);
    const slug = fan?.slug || DEFAULT_COMPANION_SLUG;

    const uniqueId = uuidv4();
    const designerUrl = `/generation/${slug}/${uniqueId}?start=true&designSession=true&userInput=${encodeURIComponent(
      prompt,
    )}`;

    if (navigateFunction) {
      navigateFunction(designerUrl);
    } else {
      window.location.href = designerUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start image generation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const startPresentationGeneration = async (
  params: StartPresentationGenerationParams,
): Promise<StartPresentationGenerationResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  try {
    const { topic } = params;
    const companionId = params.companionId || DEFAULT_COMPANION_ID;

    // Look up companion, fallback to default slug if not found
    const fan = findFanByCompanionId(companionId);
    const slug = fan?.slug || DEFAULT_COMPANION_SLUG;

    const uniqueId = uuidv4();
    const generationUrl = `/generation/${slug}/${uniqueId}?start=true&presentationSession=true&userInput=${encodeURIComponent(
      topic,
    )}`;

    createPresentation({ topic, uniqueId }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('Failed to create presentation:', error);
      }
    });

    if (navigateFunction) {
      navigateFunction(generationUrl);
    } else {
      window.location.href = generationUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return {
      success: true,
      taskId: uniqueId,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start presentation generation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const startBusinessPlanGeneration = async (
  params: StartBusinessPlanGenerationParams,
): Promise<StartBusinessPlanGenerationResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  try {
    const { topic } = params;
    const companionId = params.companionId || DEFAULT_COMPANION_ID;

    const fan = findFanByCompanionId(companionId);

    if (!fan) {
      return {
        success: false,
        error: `Companion with ID ${companionId} not found`,
      };
    }

    const uniqueId = uuidv4();
    const generationUrl = `/generation/${
      fan.slug
    }/${uniqueId}?start=true&businessPlanSession=true&userInput=${encodeURIComponent(
      topic,
    )}`;

    createBusinessPlan({ topic, uniqueId }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('Failed to create business plan:', error);
      }
    });

    if (navigateFunction) {
      navigateFunction(generationUrl);
    } else {
      window.location.href = generationUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return {
      success: true,
      taskId: uniqueId,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start business plan generation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const startWebpageGeneration = async (
  params: StartWebpageGenerationParams,
): Promise<StartWebpageGenerationResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  try {
    const { topic } = params;
    const companionId = params.companionId || DEFAULT_COMPANION_ID;

    // Look up companion, fallback to default slug if not found
    const fan = findFanByCompanionId(companionId);
    const slug = fan?.slug || DEFAULT_COMPANION_SLUG;

    const uniqueId = uuidv4();
    const generationUrl = `/generation/${slug}/${uniqueId}?start=true&webpageSession=true&userInput=${encodeURIComponent(
      topic,
    )}`;

    createWebpage({ topic, uniqueId }).catch((error) => {
      if (import.meta.env.DEV) {
        console.error('Failed to create webpage:', error);
      }
    });

    if (navigateFunction) {
      navigateFunction(generationUrl);
    } else {
      window.location.href = generationUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return {
      success: true,
      taskId: uniqueId,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start webpage generation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const startSpacesUseCase = async (
  params: StartSpacesUseCaseParams,
): Promise<StartSpacesUseCaseResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  const validTypes: SpacesUseCaseType[] = ['hospitality', 'retail', 'transit'];
  const type =
    params.type && validTypes.includes(params.type)
      ? params.type
      : 'hospitality';

  try {
    const spacesUrl = `/station/spaces?type=${encodeURIComponent(type)}`;

    if (navigateFunction) {
      navigateFunction(spacesUrl);
    } else {
      window.location.href = spacesUrl;
      return {
        success: false,
        error: 'Navigation not initialized',
      };
    }

    return { success: true };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to start spaces use case:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
